<template>
  <div>
    <members-export-dialog v-if="showExportMembersDialog" />
    <members-export-run-dialog
      v-if="showExportMembersRunDialog"
      :task="export_result"
    />
    <members-export-add-attributes-dialog
      v-if="showExportMembersAttributesDialog"
    />
    <rest-list :payload="fetchEndpoint">
      <template>
        <rest-list-column
          prop="title"
          :title="$t('Audience_Segment_ListField_Title')"
          :filterable="true"
          :route="segmentOverviewRoute"
          :min-width="300"
        />

        <rest-list-column-boolean
          prop="is_shared"
          :title="$t('Audience_Segment_ListField_IsShared')"
          v-if="segmentSharing"
        />

        <rest-list-column-actions v-if="!readonly">
          <template slot-scope="scope">
            <rest-list-action-update :route="rowUpdateRoute(scope.row)" />
            <rest-list-action-copy :route="rowCopyRoute(scope.row)" />
            <rest-list-action-delete
              :delete-message="
                $t('Audience_Segment_DeletePrompt', { label: scope.row.title })
              "
              :endpoint="rowDeleteEndpoint(scope.row)"
            />
            <rest-list-action
              v-if="$can('UserPersonalData::Export')"
              :click="actionExport"
              :row="scope.row"
              :title="$t('Audiences_Segment_Action_Export_Members')"
              icon="el-icon-download"
            />
          </template>
        </rest-list-column-actions>
      </template>
    </rest-list>
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListActionCopy from '@/common/components/rest/RestListActionCopy'
import RestListActionUpdate from '@/common/components/rest/RestListActionUpdate'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListAction from '@/common/components/rest/RestListAction'
import MembersExportRunDialog from '@/modules/audiences/components/MembersExportRunDialog'
import MembersExportAddAttributesDialog from '@/modules/audiences/components/MembersExportAddAttributesDialog'
import MembersExportDialog from '@/modules/audiences/components/MembersExportDialog'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean.vue'

export default {
  components: {
    MembersExportDialog,
    MembersExportAddAttributesDialog,
    MembersExportRunDialog,
    RestListAction,
    RestListColumnActions,
    RestListActionUpdate,
    RestListActionCopy,
    RestList,
    RestListColumn,
    RestListActionDelete,
    RestListColumnBoolean
  },
  mounted() {
    EventBus.$on('export-members-show', this.exportMembersShow)
    EventBus.$on('export-members-hide', this.exportMembersHide)
    EventBus.$on('export-members-run-show', this.exportMembersRunShow)
    EventBus.$on('export-members-run-hide', this.exportMembersRunHide)
    EventBus.$on('export-members-cancel', this.exportMembersCancel)
    EventBus.$on(
      'export-members-add-attributes-show',
      this.exportMembersAddAttributesShow
    )
    EventBus.$on(
      'export-members-add-attributes-hide',
      this.exportMembersAddAttributesHide
    )
  },

  destroyed() {
    EventBus.$off('export-members-show')
    EventBus.$off('export-members-hide')
    EventBus.$off('export-members-run-show')
    EventBus.$off('export-members-run-hide')
    EventBus.$off('export-members-add-attributes-show')
    EventBus.$off('export-members-add-attributes-hide')
    EventBus.$off('export-members-cancel')
  },
  data() {
    return {
      taskId: null,
      showExportMembersDialog: false,
      showExportMembersRunDialog: false,
      showExportMembersAttributesDialog: false,
      exportParams: {},
      segmentToExport: {},
      export_result: {}
    }
  },
  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `segments`,
        params: {
          sort: 'title',
          type: 0,
          'per-page': 50
        }
      })
    },

    exportEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/export`
      })
    },

    taskEndpoint() {
      return projectEndpoint({
        url: `task-histories/${this.taskId}?expand=task`
      })
    },

    readonly() {
      return !this.$can('Segment::Update')
    },

    segmentSharing() {
      return this.$store.state.app.project.company.segment_sharing
    }
  },

  methods: {
    actionExport(segment) {
      this.segmentToExport = segment
      this.showExportMembersDialog = true
    },
    exportMembersShow() {
      this.showExportMembersDialog = true
    },

    exportMembersHide() {
      this.showExportMembersDialog = false
    },

    exportMembersCancel() {
      this.exportMembersHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    exportMembersRunShow(params) {
      Object.assign(this.exportParams, params)
      this.showExportMembersDialog = false
      this.showExportMembersRunDialog = true
      this.exportMembers()
    },

    exportMembersRunHide() {
      this.showExportMembersRunDialog = false
      this.exportParams = {}
      this.export_result = {}
      clearInterval(this.taskLoop)
      this.taskId = null
    },

    exportMembersAddAttributesShow(params) {
      this.exportParams = params
      this.showExportMembersAttributesDialog = true
    },

    exportMembersAddAttributesHide() {
      this.showExportMembersAttributesDialog = false
    },

    exportMembers() {
      let evaluateData = {
        ...this.segmentToExport,
        ...this.exportParams
      }
      RestApi.post(this.exportEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchExportTask()
          this.taskLoop = setInterval(() => {
            this.fetchExportTask()
          }, 2000)
        })
        .catch(error => {
          this.showExportMembersDialog = false
          this.showExportMembersAttributesDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchExportTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.export_result = data
      })
    },

    handleTaskStatus(task, success, error) {
      switch (task.status) {
        case 2:
          clearInterval(this.taskLoop)
          success()
          break

        case 3:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Error'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break

        case 4:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Fatal'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break
      }
    },

    segmentOverviewRoute(row) {
      return projectRoute({
        name: 'AudienceSegmentOverview',
        query: {
          id: row.id
        }
      })
    },
    rowUpdateRoute(row) {
      return projectRoute({
        name: 'AudienceSegmentUpdate',
        query: { id: row.id }
      })
    },

    rowCopyRoute(row) {
      return projectRoute({
        name: 'AudienceSegmentCopy',
        query: { id: row.id }
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `segments/${row.id}`
      })
    }
  },
  watch: {
    export_result: {
      handler(result) {
        if (result) {
          this.handleTaskStatus(
            result,
            () => {},
            () => {
              this.exportMembersHide()
              this.exportMembersRunHide()
            }
          )
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/audiences/pages/SegmentList.vue